import AddIcon from '@/app/icons/add'
import Button from '@/components/button'
import UserContext from '@/context/user-context'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { useContext } from 'react'
import { ClickToShowModal } from '../modal'
import { cardClassName, CollectionLabel } from './ui'

type Props = { className?: string; channel: IChannel; hideLabel?: boolean }
const AddToCollectionCallout = ({ className, channel, hideLabel }: Props) => {
    const { user } = useContext(UserContext)
    const canEdit = !!(user && (user.is_staff || user.username === channel.user.username))
    return canEdit ? (
        <ClickToShowModal
            modal={{ name: 'addGifsToCollection', channel, channelId: channel.id }}
            className={cn(
                cardClassName,
                `hover:border-giphyLightCharcoal basis-full`,
                'hidden items-center justify-center md:flex',
                className
            )}
        >
            <AddIcon className={cn('size-5', hideLabel && 'size-8')} />
            {!hideLabel && <CollectionLabel label={`Add to ${channel?.display_name}`} />}
        </ClickToShowModal>
    ) : null
}

export const EmptyCollectionCallout = ({}: Props) => {
    return (
        <div className="hidden flex-1 flex-col items-center justify-center md:flex">
            <img src="https://media0.giphy.com/media/ViHbdDMcIOeLeblrbq/200w.webp" alt="" width={90} height={90} />
            <h1 className={`${NexaBlack.className}`}>There&apos;s nothing here yet</h1>
            <p className="my-2">
                Add Gifs to a Collection by clicking the{' '}
                <AddIcon className="mx-1 inline-block size-3 [--stop-color-1:var(--giphy-yellow)] [--stop-color-2:var(--giphy-red)]" />{' '}
                icon on a Gif page.
            </p>
            <div>At the moment, you can only add your own content to Collections.</div>
        </div>
    )
}

export const NewUserCallout = ({}: Props) => {
    return (
        <div className="hidden w-full flex-1 flex-col items-center justify-center gap-4 md:flex">
            <svg width="150" height="150" viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M75 149C115.869 149 149 115.869 149 75C149 34.1309 115.869 1 75 1C34.1309 1 1 34.1309 1 75C1 115.869 34.1309 149 75 149Z"
                    fill="url(#paint0_radial_3443_67111)"
                    stroke="url(#paint1_linear_3443_67111)"
                    strokeWidth="2"
                />
                <mask id="mask0_3443_67111" maskUnits="userSpaceOnUse" x="57" y="57" width="36" height="36">
                    <path
                        d="M78.25 71.75V59.4H71.75V71.75H59.4V78.25H71.75V90.6H78.25V78.25H90.6V71.75H78.25ZM93 69.35V80.65H80.65V93H69.35V80.65H57V69.35H69.35V57H80.65V69.35H93Z"
                        fill="white"
                    />
                </mask>
                <g mask="url(#mask0_3443_67111)">
                    <path
                        d="M78.25 71.75V59.4H71.75V71.75H59.4V78.25H71.75V90.6H78.25V78.25H90.6V71.75H78.25ZM93 69.35V80.65H80.65V93H69.35V80.65H57V69.35H69.35V57H80.65V69.35H93Z"
                        fill="url(#paint2_linear_3443_67111)"
                    />
                </g>
                <defs>
                    <radialGradient
                        id="paint0_radial_3443_67111"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="translate(-70.1299 75) rotate(90) scale(73.4141 71.5462)"
                    >
                        <stop />
                        <stop offset="0.405749" />
                        <stop offset="1" stopColor="#002013" />
                    </radialGradient>
                    <linearGradient
                        id="paint1_linear_3443_67111"
                        x1="20.237"
                        y1="120.908"
                        x2="149"
                        y2="-5.68561"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#00FF99" />
                        <stop offset="1" stopColor="#00CCFF" />
                    </linearGradient>
                    <linearGradient
                        id="paint2_linear_3443_67111"
                        x1="51.9581"
                        y1="97.3667"
                        x2="93"
                        y2="57"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stopColor="#00CCFF" />
                        <stop offset="1" stopColor="#00FF99" />
                    </linearGradient>
                </defs>
            </svg>
            <div className="flex flex-col gap-3">
                <h1 className={`${NexaBlack.className}`}>There&apos;s nothing here yet</h1>
                <p className="text-center">Create your first GIF to get started!</p>
                <div className="flex justify-center gap-4">
                    <Button href="/upload" isExternal className="bg-gradient-grey px-6">
                        Upload
                    </Button>
                    <Button href="/create" isExternal className="bg-gradient-grey px-6">
                        Create
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default AddToCollectionCallout
